// Retries a request when AppSync reports a resolver-level ExecutionTimeout in the response
// body or the request rejects outright. Takes a thunk so every attempt issues a fresh request.
// Lambda-level timeouts and network errors are also retried by Apollo's RetryLink; this layer
// covers the resolver-level ExecutionTimeout that RetryLink sees as a successful response.
const isTimeoutResponse = (response) =>
  response?.errors?.[0]?.errorType === 'ExecutionTimeout' ||
  // Lambda-level timeouts are normally promoted to thrown errors by the Apollo link chain and
  // arrive via the catch below; this response-body check only matters for the operations the
  // promote link excludes (rewritePolicy, generateRenewal, runRenewalUW)
  response?.errors?.[0]?.errorType === 'Lambda:ExecutionTimeoutException';

// Only re-issue rejections that can plausibly succeed on retry: network failures and the
// timeout errors the Apollo link chain promotes to rejections. Deterministic GraphQL errors
// (validation, auth, business rules) fail fast — retrying them just delays the error and,
// for mutations, risks duplicate side effects.
const isRetryableRejection = (error) =>
  Boolean(error?.networkError) || /timed out|took too long/i.test(error?.message ?? '');

export const withRetry = async (request, requestName = 'request', maxAttempts = 5) => {
  let lastTimeoutResponse;
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    if (attempt > 1) {
      console.log(`${requestName} attempt ${attempt} of ${maxAttempts}`);
    }
    try {
      const response = await request();
      if (!isTimeoutResponse(response)) {
        return response;
      }
      lastTimeoutResponse = response;
    } catch (error) {
      if (!isRetryableRejection(error) || attempt === maxAttempts) {
        throw error;
      }
    }
  }
  return lastTimeoutResponse;
};
